import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import surveyService from '../../services/surveyService';
import NotificationModal from '../reservations/components/NotificationModal';

const SurveysPage = () => {
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('📋 Carregando surveys e stats...');
      const [surveysData, statsData] = await Promise.all([
        surveyService.getHotelSurveys(),
        surveyService.getSurveyStats()
      ]);
      console.log('📋 Surveys:', surveysData);
      console.log('📋 Stats:', statsData);
      setSurveys(Array.isArray(surveysData) ? surveysData : []);
      setStats(statsData || null);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setSurveys([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-success';
    if (rating >= 3) return 'text-warning';
    return 'text-destructive';
  };

  const getRecommendationIcon = (recommended) => {
    return recommended ? (
      <Icon name="ThumbsUp" size={20} className="text-success" />
    ) : (
      <Icon name="ThumbsDown" size={20} className="text-destructive" />
    );
  };

  const calculateAverageRating = (survey) => {
    if (!survey.surveyResponse) return 0;
    
    const { overallRating, questionRatings } = survey.surveyResponse;
    
    // Se não há perguntas parametrizadas, retornar apenas a avaliação geral
    if (!questionRatings || Object.keys(questionRatings).length === 0) {
      return overallRating || 0;
    }
    
    // Calcular média incluindo avaliação geral e perguntas
    const ratings = [overallRating];
    ratings.push(...Object.values(questionRatings));
    
    const sum = ratings.reduce((acc, rating) => acc + rating, 0);
    return sum / ratings.length;
  };

  const viewSurveyDetails = (survey) => {
    setSelectedSurvey(survey);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onCollapseChange={setIsSidebarCollapsed}
      />
      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 md:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Avaliações de Satisfação</h1>
                <p className="text-muted-foreground mt-1">
                  Gerencie avaliações e feedback dos hóspedes
                </p>
              </div>
            </div>

            {/* Statistics */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total de Avaliações</p>
                      <p className="text-2xl font-bold text-foreground">{stats?.totalResponses || 0}</p>
                    </div>
                    <Icon name="Star" size={32} className="text-primary" />
                  </div>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Nota Média</p>
                      <p className={`text-2xl font-bold ${getRatingColor(stats?.averageRating || 0)}`}>
                        {(stats?.averageRating || 0).toFixed(1)}/5
                      </p>
                    </div>
                    <Icon name="TrendingUp" size={32} className="text-success" />
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Taxa de Recomendação</p>
                      <p className="text-2xl font-bold text-success">{stats?.recommendationRate || 0}%</p>
                    </div>
                    <Icon name="ThumbsUp" size={32} className="text-success" />
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Recomendaram</p>
                      <p className="text-2xl font-bold text-foreground">
                        {stats?.recommendationCount || 0}/{stats?.totalResponses || 0}
                      </p>
                    </div>
                    <Icon name="Users" size={32} className="text-primary" />
                  </div>
                </div>
              </div>
            )}

            {/* Surveys List */}
            <div className="bg-card border border-border rounded-lg">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground">Avaliações Recebidas</h2>
              </div>

              {!Array.isArray(surveys) || surveys.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="Star" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Nenhuma avaliação recebida ainda</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-foreground">Hóspede</th>
                        <th className="text-left p-4 text-sm font-semibold text-foreground">Quarto</th>
                        <th className="text-left p-4 text-sm font-semibold text-foreground">Checkout</th>
                        <th className="text-left p-4 text-sm font-semibold text-foreground">Avaliação</th>
                        <th className="text-left p-4 text-sm font-semibold text-foreground">Data</th>
                        <th className="text-center p-4 text-sm font-semibold text-foreground">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(surveys) && surveys.filter(s => s.surveyResponse).map((survey) => (
                        <tr key={survey.id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-4 text-foreground">
                            {survey.reservation?.guestName || 'N/A'}
                          </td>
                          <td className="p-4 text-foreground">
                            {survey.reservation?.room?.number || 'N/A'}
                          </td>
                          <td className="p-4 text-muted-foreground">
                            {survey.reservation?.checkOutDate 
                              ? new Date(survey.reservation.checkOutDate).toLocaleDateString('pt-BR')
                              : 'N/A'}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {(() => {
                                const averageRating = calculateAverageRating(survey);
                                return (
                                  <>
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Icon
                                        key={i}
                                        name="Star"
                                        size={16}
                                        fill={i < averageRating ? 'currentColor' : 'none'}
                                        className={getRatingColor(averageRating)}
                                      />
                                    ))}
                                    <span className="text-sm font-medium text-foreground ml-1">
                                      {averageRating.toFixed(1)}/5
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="p-4 text-muted-foreground">
                            {survey.completedAt 
                              ? new Date(survey.completedAt).toLocaleDateString('pt-BR')
                              : '-'}
                          </td>
                          <td className="p-4 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewSurveyDetails(survey)}
                              title="Ver detalhes"
                            >
                              <Icon name="Eye" size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>

      {/* Details Modal */}
      {showDetailsModal && selectedSurvey?.surveyResponse && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">Detalhes da Avaliação</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowDetailsModal(false)}>
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Informações do Hóspede */}
              <div className="bg-muted/20 rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-3">Informações da Estadia</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Hóspede:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {selectedSurvey.reservation?.guestName}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Quarto:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {selectedSurvey.reservation?.room?.number}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Checkout:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {selectedSurvey.reservation?.checkOutDate 
                        ? new Date(selectedSurvey.reservation.checkOutDate).toLocaleDateString('pt-BR')
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Data da Avaliação:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {selectedSurvey.completedAt 
                        ? new Date(selectedSurvey.completedAt).toLocaleDateString('pt-BR')
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Avaliação Geral */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Avaliação Geral</h3>
                <div className="flex items-center gap-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={24}
                      fill={i < selectedSurvey.surveyResponse.overallRating ? 'currentColor' : 'none'}
                      className={getRatingColor(selectedSurvey.surveyResponse.overallRating)}
                    />
                  ))}
                  <span className="ml-2 text-lg font-bold text-foreground">
                    {selectedSurvey.surveyResponse.overallRating}/5
                  </span>
                </div>
              </div>

              {/* Recomendação */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Recomendaria o hotel?</h3>
                <div className="flex items-center gap-2">
                  {getRecommendationIcon(selectedSurvey.surveyResponse.wouldRecommend)}
                  <span className="font-medium text-foreground">
                    {selectedSurvey.surveyResponse.wouldRecommend === true ? 'Sim' : 'Não'}
                  </span>
                </div>
              </div>

              {/* Perguntas Específicas */}
              {selectedSurvey.surveyResponse.questionRatings && Object.keys(selectedSurvey.surveyResponse.questionRatings).length > 0 && (
                <div>
                  <h3 className="font-medium text-foreground mb-3">Perguntas Específicas</h3>
                  <div className="space-y-3">
                    {Object.entries(selectedSurvey.surveyResponse.questionRatings).map(([questionId, rating]) => {
                      const template = selectedSurvey.questionTemplates?.[questionId];
                      return (
                        <div key={questionId} className="bg-muted/20 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-foreground">
                              {template?.question || `Pergunta ${questionId}`}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {rating}/5
                            </span>
                          </div>
                          {template?.description && (
                            <p className="text-xs text-muted-foreground mb-2">
                              {template.description}
                            </p>
                          )}
                          <div className="flex gap-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Icon
                                key={i}
                                name="Star"
                                size={16}
                                fill={i < rating ? 'currentColor' : 'none'}
                                className="text-primary"
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Comentários */}
              {selectedSurvey.surveyResponse.comments && (
                <div>
                  <h3 className="font-medium text-foreground mb-3">Comentários</h3>
                  <div className="bg-muted/20 rounded-lg p-4">
                    <p className="text-foreground whitespace-pre-wrap">
                      {selectedSurvey.surveyResponse.comments}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border">
              <Button onClick={() => setShowDetailsModal(false)} className="w-full">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        type={notificationData?.type || 'success'}
        title={notificationData?.title || ''}
        message={notificationData?.message || ''}
        details={notificationData?.details}
        onClose={() => setShowNotification(false)}
      />
    </div>
  );
};

export default SurveysPage;

